import type { AdPlacement } from '../../advertisement/types/ad-slot-types'
import type { ContentBlocksAndAdSlotMarkers, DeviceData } from '../types/content-block-types'
import type { NewContentBlock } from '../stores/content-block-store'

/**
 * Represents an ad slot marker content block
 * This is injected between regular content blocks to indicate where ads should be placed
 */
export type AdSlotMarkerBlock = {
  paragraphType: '__injected_ad__',
  adSlotMarker: {
    /**
     * Unique identifier for the ad slot marker
     */
    id: string,
    /**
     * Type of ad that should be placed in this slot
     * - 'rectangle': Rectangle display ad format
     * - 'banner': Banner display ad format
     * - 'outstream': Video ads that appear within the content
     */
    adType: 'rectangle' | 'banner' | 'outstream',
    /**
     * Is ad slot enabled and for what device.
     * - []: ad slot is not enabled
     * - ["desktop"]: ad slot is enabled only for desktop
     * - ["mobile"]: ad slot is enabled only for mobile
     * - ["desktop", "mobile"]: ad slot is enabled for both
     */
    enabled: ('desktop' | 'mobile')[],
    /**
     * Index of the injected ad marker
     */
    injectedIndex?: number,
    /**
     * Total number of injected ad markers
     */
    injectedTotal?: number
  }
}

/**
 * Injects ad slot markers at appropriate positions in the content blocks array
 *
 * This function analyzes the content blocks and inserts ad slot markers based on the provided configuration.
 * It respects rules like minimum distance from the top, no ads before/after specific block types,
 * and placement of outstream ads vs. display ads.
 *
 * @param blocks - Array of content blocks to process
 * @param config - Configuration for ad placement rules
 * @param deviceData - Data about the device that is rendering the content, optional for server-side rendering
 * @returns Array of content blocks with ad slot markers injected at appropriate positions
 */
export function injectAdSlotMarkers (blocks: NewContentBlock<ContentBlocksAndAdSlotMarkers>[], placementConfig?: AdPlacement | null, deviceData?: DeviceData): NewContentBlock<ContentBlocksAndAdSlotMarkers>[] {
  if (blocks.length === 0) return []

  // Instead of returning blocks when no config is provided, define effectiveConfig with defaults
  const effectiveConfig = placementConfig || {
    firstDynamicAdAfterBlockNumber: Infinity,
    rectangleVsBannerSplit: 100,
    minimumDynamicAdDistance: 0,
    noDisplayAdBefore: [],
    noDisplayAdAfter: [],
    showOutstreamAd: false,
    noOutstreamAdBefore: [],
    noOutstreamAdAfter: [],
    placeOutstreamAfterAmountOfDisplayAds: undefined,
    minimumDistanceBetweenRelatedBlocks: undefined,
    noRelatedBlockBefore: [],
    noRelatedBlockAfter: [],
    nativeTeasersPerRelatedBlock: undefined,
    nativeTeasersPerQuadrupleTeaser: undefined
  } satisfies AdPlacement

  const firstDynamicAdAfterBlockNumber = effectiveConfig.firstDynamicAdAfterBlockNumber ?? 0
  const rectangleVsBannerSplit = effectiveConfig.rectangleVsBannerSplit ?? 100
  // Use the new field from AdPlacement for dynamic ad distance
  const minimumDynamicAdDistance = effectiveConfig.minimumDynamicAdDistance ?? 0

  const result: typeof blocks = []

  type CandidateAdMarker = {
    indexInResult: number,
    candidate: boolean,
    enabled: boolean,
    adjacentDisabled: boolean,
    originalIndex: number,
    marker: NewContentBlock<ContentBlocksAndAdSlotMarkers>,
    effectivePosition: number
  }

  // Keep track of our ad marker objects separately for further processing
  const adMarkers: CandidateAdMarker[] = []

  // Inject an ad marker at the very beginning
  const initMarker = {
    id: 'ad-slot-marker-initial',
    enabled: false,
    adType: 'rectangle'
  }
  const initAdBlock = {
    id: 'injected-initial',
    type: '__injected_ad__',
    enabled: false,
    data: { adSlotMarker: { ...initMarker, enabled: [] }, paragraphType: '__injected_ad__' },
    dimensions: { width: 0, height: 0, fromTop: 0, innerWidth: 0, innerHeight: 0 }
  } as NewContentBlock<ContentBlocksAndAdSlotMarkers>
  result.push(initAdBlock)
  adMarkers.push({
    indexInResult: result.length - 1,
    candidate: false,
    enabled: false,
    adjacentDisabled: false,
    originalIndex: -1,
    marker: initAdBlock,
    effectivePosition: 0
  })

  // For each content block, push the block and then an ad marker after it
  for (let i = 0; i < blocks.length; i++) {
    const currentBlock = blocks[i]
    if (!currentBlock) continue
    result.push(currentBlock)
    const marker = {
      id: `ad-slot-marker-${i}`,
      enabled: false,
      adType: 'rectangle'
    }
    const adBlock = {
      id: `injected-${i}`,
      type: '__injected_ad__',
      enabled: false,
      data: { adSlotMarker: { ...marker, enabled: [] }, paragraphType: '__injected_ad__' },
      dimensions: { width: 0, height: 0, fromTop: 0, innerWidth: 0, innerHeight: 0 }
    } as NewContentBlock<ContentBlocksAndAdSlotMarkers>
    result.push(adBlock)
    adMarkers.push({
      indexInResult: result.length - 1,
      candidate: false,
      enabled: false,
      adjacentDisabled: false,
      originalIndex: i,
      marker: adBlock,
      effectivePosition: 0
    })
  }

  // Determine which ad marker placeholders are candidates for dynamic ad insertion
  adMarkers.forEach(m => {
    if ((m.originalIndex + 1) >= firstDynamicAdAfterBlockNumber) {
      m.candidate = true
    }
  })

  // Apply adjacent rules regarding placement: mark ad markers as disallowed (but don't modify 'enabled' yet) if adjacent to disallowed content blocks.
  const noDisplayAdBefore = effectiveConfig.noDisplayAdBefore ?? []
  const noDisplayAdAfter = effectiveConfig.noDisplayAdAfter ?? []
  adMarkers.forEach(m => {
    const markerIndex = m.indexInResult
    const prevBlock = result[markerIndex - 1]
    const nextBlock = result[markerIndex + 1]
    if (prevBlock && noDisplayAdAfter.includes(prevBlock.type)) {
      m.adjacentDisabled = true
    }
    if (nextBlock && noDisplayAdBefore.includes(nextBlock.type)) {
      m.adjacentDisabled = true
    }
  })

  // Dynamic Ad Enablement Logic
  if (!deviceData) {
    // SSR: enable only the first candidate marker that is not adjacent disabled
    const candidateMarkers = adMarkers.filter(m => m.candidate && !m.adjacentDisabled).sort((a, b) => a.originalIndex - b.originalIndex)
    if (candidateMarkers.length > 0) {
      candidateMarkers[0]!.enabled = true
      for (let i = 1; i < candidateMarkers.length; i++) {
        candidateMarkers[i]!.enabled = false
      }
    }
  } else {
    // Generalized approach: only consider candidate markers which are not adjacent disabled
    const filteredCandidates = adMarkers.filter(m => m.candidate && !m.adjacentDisabled).sort((a, b) => a.originalIndex - b.originalIndex)
    const minDistance = (minimumDynamicAdDistance / 100) * deviceData.viewportHeight

    // Compute effective vertical position for each candidate marker
    filteredCandidates.forEach(m => {
      let pos = 0
      for (let i = 0; i <= m.originalIndex; i++) {
        const height = blocks[i]?.dimensions?.height ?? 0
        pos += height
      }
      m.effectivePosition = pos
    })

    if (filteredCandidates.length > 0) {
      if (minDistance === 0) {
        filteredCandidates.forEach(m => { m.enabled = true })
      } else {
        filteredCandidates[0]!.enabled = true
        let lastEnabledPos = filteredCandidates[0]!.effectivePosition
        const epsilon = 1e-6
        for (let i = 1; i < filteredCandidates.length; i++) {
          const gap = filteredCandidates[i]!.effectivePosition - lastEnabledPos
          if (gap - minDistance > epsilon) {
            filteredCandidates[i]!.enabled = true
            lastEnabledPos = filteredCandidates[i]!.effectivePosition
          } else {
            filteredCandidates[i]!.enabled = false
          }
        }
      }
    }
  }

  // Now, update the injected ad marker blocks with the computed enabled status
  adMarkers.forEach(m => {
    m.marker.enabled = m.enabled
    m.marker.data.adSlotMarker.enabled = m.enabled
  })

  // Update the assignment of adType for injected ad markers using a dynamic cycle based on rectangleVsBannerSplit
  function getDisplayCycle (split: number): ('rectangle' | 'banner')[] {
    if (split === 0) return ['banner']
    if (split === 100) return ['rectangle']

    if (split < 50) {
      const cycleLength = Math.round(100 / split)
      // pattern: cycleLength-1 times 'banner' then 'rectangle'
      return Array(cycleLength - 1).fill('banner').concat(['rectangle'])
    } else {
      const cycleLength = Math.round(100 / (100 - split))
      // pattern: cycleLength-1 times 'rectangle' then 'banner'
      return Array(cycleLength - 1).fill('rectangle').concat(['banner'])
    }
  }

  const cycle = getDisplayCycle(rectangleVsBannerSplit)
  const enabledMarkers = adMarkers.filter(m => m.enabled).sort((a, b) => a.originalIndex - b.originalIndex)
  enabledMarkers.forEach((m, idx) => {
    m.marker.data.adSlotMarker.adType = cycle[idx % cycle.length]
  })

  if (effectiveConfig.showOutstreamAd && effectiveConfig.placeOutstreamAfterAmountOfDisplayAds) {
    const groupSize = effectiveConfig.placeOutstreamAfterAmountOfDisplayAds + 1
    let enabledCounter = 0
    enabledMarkers.forEach(m => {
      enabledCounter++
      if (enabledCounter % groupSize === 0) {
        const markerIndex = m.indexInResult
        const prevBlock = result[markerIndex - 1]?.type
        const nextBlock = result[markerIndex + 1]?.type
        const noOutstreamBefore = effectiveConfig.noOutstreamAdBefore || []
        const noOutstreamAfter = effectiveConfig.noOutstreamAdAfter || []
        let adType: 'rectangle' | 'banner' | 'outstream' = 'outstream'
        if ((prevBlock && noOutstreamAfter.includes(prevBlock)) || (nextBlock && noOutstreamBefore.includes(nextBlock))) {
          adType = 'rectangle'
        }
        m.marker.data.adSlotMarker.adType = adType
      }
    })
  }

  adMarkers.forEach(m => {
    if (!m.enabled) {
      m.marker.data.adSlotMarker.adType = 'rectangle'
    }
  })

  // Annotate each enabled injected ad marker with injectedIndex and injectedTotal per ad type group
  const enabledAdMarkers = result.filter(block => block.type === '__injected_ad__' && block.enabled) as NewContentBlock<AdSlotMarkerBlock>[]
  const groups = enabledAdMarkers.reduce((acc: Record<string, NewContentBlock<AdSlotMarkerBlock>[]>, marker) => {
    const adType = marker.data.adSlotMarker.adType
    if (!acc[adType]) {
      acc[adType] = []
    }
    acc[adType].push(marker)
    return acc
  }, {})

  Object.keys(groups).forEach(adType => {
    const markers = groups[adType]
    const total = markers.length
    markers.forEach((marker, index) => {
      marker.data.adSlotMarker.injectedIndex = index
      marker.data.adSlotMarker.injectedTotal = total
    })
  })

  return result
}
